import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
    DropdownButton,
    InputGroup
} from 'react-bootstrap';
import Tooltip from '@sumologic/yama-ds-components/components/Tooltip';

import { checkOverflow } from '~/modules/helpers/element';

import Icon from '~/components/Icon/Icon';
import ListGrid from '~/components/ListGrid3/ListGrid3';
import Translate from '~/components/Translate/Translate';

//----------------------------------------------------------------------------------------------------------------------

const ListItem = ({ data, onClick, itemActions }) => {
    const itemLabel = data?.name;
    // 𝘰𝘯𝘭𝘺 𝘪𝘵𝘦𝘮𝘴 𝘸𝘪𝘵𝘩 𝘢 𝘭𝘢𝘣𝘦𝘭 𝘸𝘪𝘭𝘭 𝘣𝘦 𝘥𝘪𝘴𝘱𝘭𝘢𝘺𝘦𝘥
    if (itemLabel) {
        const itemSelected = data.selected;
        return (
            <div className="item-content text-truncate">
                <span
                    className={`label-text ${
                        itemSelected ? 'selected' : 'clickable'
                    }`}
                    onClick={
                        itemSelected
                            ? null
                            : () => {
                                  onClick(data);
                              }
                    }
                >
                    {itemLabel}
                </span>
                <span className="icons icons-right">
                    {itemActions.map((action, index) => {
                        if (action) {
                            const {
                                tip,
                                tipFn,
                                iconType,
                                iconTypeFn,
                                buttonClass,
                                iconClickFn,
                                testid
                            } = action;
                            const type = iconTypeFn?.(data) || iconType;
                            if (type) {
                                const content = (
                                    <button
                                        key={index}
                                        disabled={!iconClickFn}
                                        data-testid={
                                            testid ? `${testid}-${index}` : null
                                        }
                                        className={`icon-btn action ${buttonClass}`}
                                        onClick={event => {
                                            event.stopPropagation();
                                            iconClickFn?.(data, event);
                                        }}
                                    >
                                        <Icon type={type} />
                                    </button>
                                );
                                const toolTipMessage = tipFn?.(data) || tip;
                                return toolTipMessage ? (
                                    <Tooltip
                                        key={index}
                                        title={<Translate message={toolTipMessage} />}
                                        placement="left"
                                        arrow
                                    >
                                        {content}
                                    </Tooltip>
                                ) : (
                                    content // 𝘸𝘪𝘵𝘩𝘰𝘶𝘵 𝘵𝘪𝘱𝘴
                                );
                            }
                        }
                    })}
                </span>
            </div>
        );
    }
};

ListItem.propTypes = {
    data: PropTypes.object,
    onClick: PropTypes.func,
    itemActions: PropTypes.array.isRequired
};

//----------------------------------------------------------------------------------------------------------------------

const QueryList = ({
    queries,
    updateQueries,
    filterValue,
    enableBookmarks
}) => {
    const ref = useRef({}).current;
    const [listIsOpen, setListIsOpen] = useState(false);

    let selectedQuery;
    let filterIsQuery;

    const hasFilter = !!filterValue;
    const hasQueries = queries?.length > 0;

    if (hasQueries) {
        for (const query of queries) {
            if (query) {
                if (
                    query.selected // 𝘤𝘧𝘳 PreferencesManager selectedQueryMatcher
                ) {
                    selectedQuery = query;
                    if (!hasFilter || filterIsQuery) {
                        break;
                    }
                }
                if (hasFilter && filterValue === query.value) {
                    filterIsQuery = true;
                    if (selectedQuery) {
                        break;
                    }
                }
            }
        }
    }

    const filterIsSelectedQuery =
        filterIsQuery && !!selectedQuery && filterValue === selectedQuery.value;

    const listLabel = filterIsSelectedQuery ? (
        <span>{selectedQuery?.name || null}</span>
    ) : (
        <Translate message="Query" />
    );

    const itemActions = hasQueries && [
        {
            testid: 'delete-query',
            buttonClass: 'delete-action', // -> when test use testid, rename class to: 'delete-query' (.icon-btn.action.delete-query)
            tip: 'Delete query',
            iconType: 'trash2',
            iconClickFn: (targetQuery, event) => {
                const { name } = targetQuery;
                const newQueries = queries.filter(e => name !== e.name);
                updateQueries(newQueries);
                if (!event?.shiftKey) {
                    // 𝘵𝘳𝘪𝘨𝘨𝘦𝘳 𝘤𝘭𝘪𝘤𝘬 𝘵𝘰 𝘤𝘭𝘰𝘴𝘦 𝘭𝘪𝘴𝘵
                    ref.dropdownEl?.click();
                }
            }
        },
        enableBookmarks && {
            testid: 'toggle-bookmark',
            buttonClass: 'bookmark-action', // -> when test use testid, rename class to: 'toggle-bookmark' (.icon-btn.action.toggle-bookmark)
            tipFn: e => `${e.bookmark ? 'Remove' : 'Add'} bookmark`,
            iconTypeFn: e => `bookmark2 ${e.bookmark ? 'text-primary' : ''}`,
            iconClickFn: targetQuery => {
                const { name, value } = targetQuery;
                const newQueries = queries.map(query => {
                    if (
                        name === query.name &&
                        value === query.value // 𝘶𝘯𝘪𝘲𝘶𝘦𝘯𝘦𝘴𝘴 𝘤𝘩𝘦𝘤𝘬
                    ) {
                        query.bookmark = !targetQuery.bookmark; // 𝘵𝘰𝘨𝘨𝘭𝘦
                    }
                    return query;
                });
                updateQueries(newQueries);
            }
        }
    ];

    const listItemClickHandler = data => {
        const newQueries = [];
        if (queries?.length) {
            let updated;
            const { name, value } = data;
            for (const query of queries) {
                if (query) {
                    let item = query;
                    if (
                        name === query.name &&
                        value === query.value // 𝘶𝘯𝘪𝘲𝘶𝘦𝘯𝘦𝘴𝘴 𝘤𝘩𝘦𝘤𝘬
                    ) {
                        item = updated = { ...query, ...data, selected: true };
                    } else {
                        query.selected = false;
                    }
                    newQueries.push(item);
                }
            }
            // console.log({ queries, data, updated, newQueries });
            updateQueries(newQueries);
        }
    };

    return (
        <div
            ref={el => {
                ref.dropdownEl = el;
            }}
            data-testid="query-list-wrapper"
            className="query-selection d-flex dropdown input-group-append"
        >
            <DropdownButton
                as={InputGroup.Append}
                onToggle={setListIsOpen}
                className="query-selection"
                variant="outline-light"
                alignRight
                title={
                    <div data-testid="query-list-toggler-label">
                        <div
                            className="text-truncate"
                            ref={el => {
                                ref.labelWrapper = el;
                            }}
                        >
                            {listIsOpen || !checkOverflow(ref.labelWrapper) ? (
                                listLabel
                            ) : (
                                <Tooltip
                                    title={listLabel}
                                    placement="bottom"
                                    arrow
                                >
                                    {listLabel}
                                </Tooltip>
                            )}
                        </div>
                    </div>
                }
            >
                {hasQueries && (
                    <ListGrid
                        data-testid="query-list"
                        key={queries.timestamp}
                        items={queries}
                        kind="queries"
                        isDraggable={true}
                        onSortChange={(moved, flat) => {
                            updateQueries(flat);
                        }}
                        handleRenderer={() => {
                            return <Icon type="menu2 handle" />;
                        }}
                        itemRenderer={(data, dragHandleProps) => {
                            return (
                                <ListItem
                                    dragHandleProps={dragHandleProps}
                                    onClick={listItemClickHandler}
                                    itemActions={itemActions}
                                    data={data}
                                />
                            );
                        }}
                    />
                )}
            </DropdownButton>
        </div>
    );
};

QueryList.propTypes = {
    queries: PropTypes.array,
    updateQueries: PropTypes.func,
    selectQuery: PropTypes.func,
    filterValue: PropTypes.string,
    enableBookmarks: PropTypes.bool
};

QueryList.defaultProps = {
    queries: []
};

export default QueryList;
